import React, { useEffect, useMemo, useState } from 'react'
import { Input } from 'reactstrap'
import Breadcrumbs from '../../components/Common/Breadcrumb'
import CATALOG, { flattenCatalog } from './catalog'
import ATOM_USAGE from './atomUsage'
import FORMS from './forms'
import SCREENS from './screens'
import MIV_CATALOG, { flattenMivCatalog } from './mivCatalog'
import LAYOUT_CATALOG, { flattenLayoutCatalog } from './layoutCatalog'
import VariantMock from './Mocks'
import FormMock from './FormMocks'
import ScreenMock from './ScreenMocks'
import MivMock from './MivMocks'
import LayoutMock from './LayoutMocks'
import OikosLiveShell from './OikosLiveShell'
import useComponenteErpMap from './useComponenteErpMap'
import ErpDesignCatalogGate from './ErpDesignCatalogGate'
import {
  clearErpDesignCatalogAuth,
  isErpDesignCatalogAuthed,
} from './erpDesignCatalogAuth'
import './erpDesignCatalog.css'

const LAYERS = [
  { id: 'brand', label: 'Identidade visual', hint: 'MIV 2026' },
  { id: 'layouts', label: 'Layouts / grids', hint: 'UI Card · tipos de tela' },
  { id: 'components', label: 'Componentes', hint: 'L1 · átomos' },
  { id: 'forms', label: 'Formulários', hint: 'L2 · composição' },
  { id: 'screens', label: 'Telas', hint: 'L3 · páginas' },
]

function matchQuery(text, q) {
  return text.toLowerCase().includes(q)
}

function buildNav(filter) {
  const q = filter.trim().toLowerCase()
  const components = []
  CATALOG.forEach((g) => {
    const items = g.items.filter((it) => {
      if (!q) return true
      return (
        matchQuery(it.nome, q) ||
        matchQuery(it.paraQue, q) ||
        matchQuery(g.grupo, q) ||
        it.variantes.some((v) => matchQuery(v.nome, q))
      )
    })
    if (items.length) components.push({ grupo: g.grupo, items })
  })

  const forms = FORMS.filter((f) => {
    if (!q) return true
    return (
      matchQuery(f.nome, q) ||
      matchQuery(f.paraQue, q) ||
      f.usa.some((u) => matchQuery(u, q)) ||
      f.variantes.some((v) => matchQuery(v.nome, q))
    )
  })

  const screens = SCREENS.filter((s) => {
    if (!q) return true
    return (
      matchQuery(s.nome, q) ||
      matchQuery(s.paraQue, q) ||
      s.usa.some((u) => matchQuery(u, q)) ||
      s.variantes.some((v) => matchQuery(v.nome, q))
    )
  })

  const brand = []
  MIV_CATALOG.forEach((g) => {
    const items = g.items.filter((it) => {
      if (!q) return true
      return (
        matchQuery(it.nome, q) ||
        matchQuery(it.paraQue, q) ||
        matchQuery(g.grupo, q) ||
        it.variantes.some((v) => matchQuery(v.nome, q))
      )
    })
    if (items.length) brand.push({ grupo: g.grupo, items })
  })

  const layouts = []
  LAYOUT_CATALOG.forEach((g) => {
    const items = g.items.filter((it) => {
      if (!q) return true
      return (
        matchQuery(it.nome, q) ||
        matchQuery(it.paraQue, q) ||
        matchQuery(g.grupo, q) ||
        it.variantes.some((v) => matchQuery(v.nome, q))
      )
    })
    if (items.length) layouts.push({ grupo: g.grupo, items })
  })

  return { brand, layouts, components, forms, screens }
}

function firstSelection(nav) {
  if (nav.brand[0]?.items[0]) {
    return { layer: 'brand', id: nav.brand[0].items[0].id }
  }
  if (nav.layouts[0]?.items[0]) {
    return { layer: 'layouts', id: nav.layouts[0].items[0].id }
  }
  if (nav.components[0]?.items[0]) {
    return { layer: 'components', id: nav.components[0].items[0].id }
  }
  if (nav.forms[0]) return { layer: 'forms', id: nav.forms[0].id }
  if (nav.screens[0]) return { layer: 'screens', id: nav.screens[0].id }
  return null
}

function findItem(layer, id) {
  if (layer === 'brand') {
    return flattenMivCatalog().find((x) => x.id === id) || null
  }
  if (layer === 'layouts') {
    return flattenLayoutCatalog().find((x) => x.id === id) || null
  }
  if (layer === 'components') {
    const it = flattenCatalog().find((x) => x.id === id)
    if (!it) return null
    return { ...it, ...(ATOM_USAGE[it.id] || {}) }
  }
  if (layer === 'forms') return FORMS.find((f) => f.id === id) || null
  if (layer === 'screens') return SCREENS.find((s) => s.id === id) || null
  return null
}

function OikosTodayComponents({ erpHand, map, status }) {
  if (status === 'loading' || status === 'error') return null

  const list = map[erpHand] || []

  return (
    <section className="erp-cat-real-wrap" aria-label="Componentes no Oikos hoje">
      <header className="erp-cat-real-wrap__head">
        <strong>No Oikos hoje</strong>
        <span>Componentes reais que implementam este papel</span>
      </header>
      {list.length === 0 ? (
        <p className="erp-cat-real-wrap__empty">
          Nenhum componente do inventário mapeado para este papel.
        </p>
      ) : (
        <ul className="erp-cat-real-list">
          {list.map((c) => (
            <li
              key={c.id}
              className={`erp-cat-real-list__item erp-cat-real-list__item--${c.confianca}`}
              title={`${c.hand} · confiança ${c.confianca}`}
            >
              {c.nome}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function CodeDemo({ item, erpHand, map, mapStatus }) {
  return (
    <>
      {item?.usage ? (
        <section className="erp-cat-code-wrap" aria-label="Código de exemplo">
          <header className="erp-cat-code-wrap__head">
            <strong>Código de exemplo</strong>
            {item.importName && (
              <code>
                import {'{ '}
                {item.importName}
                {' }'} from &apos;@oikos/ui&apos;
              </code>
            )}
          </header>
          <pre className="erp-cat-code-block">
            <code>{item.usage}</code>
          </pre>
          <p className="erp-cat-code-wrap__note">
            API proposta — espelha a demo interativa acima (troque <code>appearance</code> para A/B/C).
          </p>
        </section>
      ) : null}
      <OikosTodayComponents erpHand={erpHand} map={map} status={mapStatus} />
    </>
  )
}

function DetailPanel({ layer, item, erpMap, erpMapStatus }) {
  const [livePreview, setLivePreview] = useState(null)

  useEffect(() => {
    if (!livePreview) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') setLivePreview(null)
    }
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prevOverflow
      window.removeEventListener('keydown', onKey)
    }
  }, [livePreview])

  if (!item) {
    return <p className="text-muted mb-0">Nenhum item com esse filtro.</p>
  }

  const isBrand = layer === 'brand'
  const isLayouts = layer === 'layouts'
  const isL1 = layer === 'components'
  const isScreens = layer === 'screens'
  const Mock = isBrand
    ? MivMock
    : isLayouts
      ? LayoutMock
      : isL1
        ? VariantMock
        : layer === 'forms'
          ? FormMock
          : ScreenMock
  const layerLabel = isBrand
    ? 'Identidade visual (MIV)'
    : isLayouts
      ? 'Layouts / grids (UI Card)'
      : layer === 'components'
        ? 'Componente (L1)'
        : layer === 'forms'
          ? 'Formulário (L2)'
          : 'Tela (L3)'

  return (
    <div className="erp-cat-detail">
      <header className="erp-cat-detail__head">
        <p className="erp-cat-grupo">
          {layerLabel}
          {item.grupo ? ` · ${item.grupo}` : ''}
        </p>
        <h2>{item.nome}</h2>
        <p>{item.paraQue}</p>
        {isL1 && (
          <p className="erp-cat-papel-note">
            Este é um <strong>papel</strong> do ERP. As 3 opções abaixo são{' '}
            <strong>estilos</strong> do mesmo papel — clique e interaja; o código fica embaixo.
          </p>
        )}
        {isBrand && (
          <p className="erp-cat-papel-note">
            Regras do <strong>Manual de Identidade Visual Ecossis 2026</strong> aplicadas em demos
            interativas (skill <code>ecossis-ui</code>).
          </p>
        )}
        {isLayouts && (
          <p className="erp-cat-papel-note">
            Tipos de tela do <strong>UI Card</strong>: organização e espaçamento das zonas antes de
            alocar componentes. Não confundir com padrões de navegação.
          </p>
        )}
        {isScreens && (
          <p className="erp-cat-papel-note">
            Cada variante abre <strong>como se estivesse dentro do Oikos de hoje</strong> (mesmo
            header + menu horizontal). Use <strong>Abrir no Oikos</strong>.
          </p>
        )}
        {!isL1 && !isBrand && !isLayouts && item.usa?.length > 0 && (
          <p className="erp-cat-usa">
            Compõe:{' '}
            {item.usa.map((u) => (
              <code key={u}>{u}</code>
            ))}
          </p>
        )}
      </header>

      <div
        className={`erp-cat-variants${
          isScreens ? ' erp-cat-variants--screens' : ''
        }${isLayouts || item.fullWidth ? ' erp-cat-variants--layouts' : ''}`}
      >
        {item.variantes.map((v) => (
          <article key={v.id} className={`erp-cat-card${isScreens ? ' erp-cat-card--screen' : ''}`}>
            <header className="erp-cat-card__head">
              <strong>{v.nome}</strong>
              <span className="erp-cat-badge">demo {v.id}</span>
            </header>
            <div className="erp-cat-card__body">
              <Mock mockId={v.mock} />
            </div>
            <footer className="erp-cat-card__foot">
              {isScreens ? (
                <button
                  type="button"
                  className="erp-cat-live-btn"
                  onClick={() =>
                    setLivePreview({ mockId: v.mock, nome: v.nome, tela: item.nome })
                  }
                >
                  Abrir no Oikos
                </button>
              ) : (
                <p className="erp-cat-card__note mb-0">
                  {isBrand || isLayouts
                    ? 'Demo de arquitetura — zonas e espaçamento.'
                    : 'Demo ao vivo — digite, clique e teste.'}
                </p>
              )}
            </footer>
          </article>
        ))}
      </div>

      {!isBrand && !isLayouts && (
        <CodeDemo
          item={item}
          erpHand={item.id}
          map={erpMap}
          mapStatus={erpMapStatus}
        />
      )}

      {livePreview && (
        <OikosLiveShell
          caseLabel={`${livePreview.tela} · ${livePreview.nome}`}
          onClose={() => setLivePreview(null)}
        >
          <ScreenMock mockId={livePreview.mockId} />
        </OikosLiveShell>
      )}
    </div>
  )
}

function layerCount(layerId, nav) {
  if (layerId === 'brand') return nav.brand.reduce((n, g) => n + g.items.length, 0)
  if (layerId === 'layouts') return nav.layouts.reduce((n, g) => n + g.items.length, 0)
  if (layerId === 'components') return nav.components.reduce((n, g) => n + g.items.length, 0)
  if (layerId === 'forms') return nav.forms.length
  return nav.screens.length
}

function NavGrouped({ groups, layer, selection, onSelect, emptyLabel }) {
  return (
    <div className="erp-cat-nav-body">
      {groups.map((g) => (
        <div key={g.grupo} className="erp-cat-nav-group">
          <div className="erp-cat-nav-group__label">{g.grupo}</div>
          {g.items.map((it) => (
            <button
              key={it.id}
              type="button"
              className={`erp-cat-nav-item${
                selection?.layer === layer && selection.id === it.id ? ' is-active' : ''
              }`}
              onClick={() => onSelect(layer, it.id)}
            >
              {it.nome}
            </button>
          ))}
        </div>
      ))}
      {!groups.length && <p className="erp-cat-nav-empty">{emptyLabel}</p>}
    </div>
  )
}

function NavFlat({ items, layer, selection, onSelect, emptyLabel }) {
  return (
    <div className="erp-cat-nav-body">
      {items.map((it) => (
        <button
          key={it.id}
          type="button"
          className={`erp-cat-nav-item${
            selection?.layer === layer && selection.id === it.id ? ' is-active' : ''
          }`}
          onClick={() => onSelect(layer, it.id)}
        >
          {it.nome}
        </button>
      ))}
      {!items.length && <p className="erp-cat-nav-empty">{emptyLabel}</p>}
    </div>
  )
}

export default function ErpDesignCatalog(props = {}) {
  const [authed, setAuthed] = useState(() => isErpDesignCatalogAuthed())

  const signOut = () => {
    clearErpDesignCatalogAuth()
    setAuthed(false)
    if (typeof props.onSignOut === 'function') props.onSignOut()
  }

  if (!authed) {
    return (
      <div className={`erp-cat-gate-root${props.standalone ? ' is-standalone' : ''}`}>
        <ErpDesignCatalogGate onAuthed={() => setAuthed(true)} />
      </div>
    )
  }

  return <ErpDesignCatalogApp {...props} onSignOut={signOut} />
}

function ErpDesignCatalogApp({
  standalone = false,
  mapSource = 'api',
  onSignOut,
} = {}) {
  const [filter, setFilter] = useState('')
  const [layerOpen, setLayerOpen] = useState({
    brand: true,
    layouts: true,
    components: true,
    forms: true,
    screens: true,
  })
  const [selection, setSelection] = useState(null)
  const { byHand: erpMap, status: erpMapStatus } = useComponenteErpMap({ source: mapSource })

  const nav = useMemo(() => buildNav(filter), [filter])
  const atoms = useMemo(() => flattenCatalog(), [])
  const brandCount = useMemo(() => flattenMivCatalog().length, [])
  const layoutCount = useMemo(() => flattenLayoutCatalog().length, [])

  useEffect(() => {
    if (!standalone) return undefined
    document.title = 'Catálogo ERP · Ecossis'
    const prev = {
      layout: document.body.getAttribute('data-layout'),
      topbar: document.body.getAttribute('data-topbar'),
      layoutSize: document.body.getAttribute('data-layout-size'),
      background: document.body.style.background,
      margin: document.body.style.margin,
    }
    document.body.removeAttribute('data-layout')
    document.body.removeAttribute('data-topbar')
    document.body.removeAttribute('data-layout-size')
    document.body.style.margin = '0'
    document.body.style.background = '#eef0f3'
    return () => {
      if (prev.layout) document.body.setAttribute('data-layout', prev.layout)
      else document.body.removeAttribute('data-layout')
      if (prev.topbar) document.body.setAttribute('data-topbar', prev.topbar)
      else document.body.removeAttribute('data-topbar')
      if (prev.layoutSize) document.body.setAttribute('data-layout-size', prev.layoutSize)
      else document.body.removeAttribute('data-layout-size')
      document.body.style.background = prev.background
      document.body.style.margin = prev.margin
    }
  }, [standalone])

  useEffect(() => {
    if (!selection) {
      setSelection(firstSelection(nav))
      return
    }
    const stillThere =
      (selection.layer === 'brand' &&
        nav.brand.some((g) => g.items.some((i) => i.id === selection.id))) ||
      (selection.layer === 'layouts' &&
        nav.layouts.some((g) => g.items.some((i) => i.id === selection.id))) ||
      (selection.layer === 'components' &&
        nav.components.some((g) => g.items.some((i) => i.id === selection.id))) ||
      (selection.layer === 'forms' && nav.forms.some((f) => f.id === selection.id)) ||
      (selection.layer === 'screens' && nav.screens.some((s) => s.id === selection.id))
    if (!stillThere) setSelection(firstSelection(nav))
  }, [nav, selection])

  const current = selection ? findItem(selection.layer, selection.id) : null

  const select = (layer, id) => setSelection({ layer, id })

  return (
    <div className={`page-content erp-cat erp-cat--shell${standalone ? ' erp-cat--standalone' : ''}`}>
      {standalone ? (
        <header className="design-manual-top">
          <div>
            <strong>Catálogo ERP</strong>
            <span>Identidade · layouts · componentes · formulários · telas</span>
          </div>
          {onSignOut ? (
            <button type="button" className="design-manual-top__out" onClick={onSignOut}>
              Sair
            </button>
          ) : null}
        </header>
      ) : (
        <div className="erp-cat-shell-top">
          <Breadcrumbs
            title="Catálogo ERP (do zero)"
            breadcrumbItems={[{ title: 'Catálogo ERP' }]}
          />
          {onSignOut ? (
            <button type="button" className="erp-cat-shell-top__out" onClick={onSignOut}>
              Sair
            </button>
          ) : null}
        </div>
      )}

      <div className="erp-cat-shell">
        <aside className="erp-cat-sidebar" aria-label="Navegação do catálogo">
          <div className="erp-cat-sidebar__brand">
            <strong>{standalone ? 'Design system ERP' : 'Oikos UI'}</strong>
            <span>{standalone ? 'Documento de referência' : 'MIV · layouts · L1–L3'}</span>
          </div>

          <Input
            type="search"
            className="erp-cat-sidebar__search"
            placeholder="Buscar…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />

          <p className="erp-cat-sidebar__stats">
            {brandCount} MIV · {layoutCount} layouts · {atoms.length} átomos · {FORMS.length} forms ·{' '}
            {SCREENS.length} telas
          </p>

          {LAYERS.map((layer) => {
            const open = layerOpen[layer.id]
            const count = layerCount(layer.id, nav)

            return (
              <div key={layer.id} className="erp-cat-nav-layer">
                <button
                  type="button"
                  className="erp-cat-nav-layer__toggle"
                  onClick={() =>
                    setLayerOpen((s) => ({ ...s, [layer.id]: !s[layer.id] }))
                  }
                  aria-expanded={open}
                >
                  <span>
                    {layer.label}
                    <small>{layer.hint}</small>
                  </span>
                  <span className="erp-cat-nav-count">
                    {count} {open ? '▾' : '▸'}
                  </span>
                </button>

                {open && layer.id === 'brand' && (
                  <NavGrouped
                    groups={nav.brand}
                    layer="brand"
                    selection={selection}
                    onSelect={select}
                    emptyLabel="Nenhum item MIV"
                  />
                )}

                {open && layer.id === 'layouts' && (
                  <NavGrouped
                    groups={nav.layouts}
                    layer="layouts"
                    selection={selection}
                    onSelect={select}
                    emptyLabel="Nenhum layout"
                  />
                )}

                {open && layer.id === 'components' && (
                  <NavGrouped
                    groups={nav.components}
                    layer="components"
                    selection={selection}
                    onSelect={select}
                    emptyLabel="Nenhum componente"
                  />
                )}

                {open && layer.id === 'forms' && (
                  <NavFlat
                    items={nav.forms}
                    layer="forms"
                    selection={selection}
                    onSelect={select}
                    emptyLabel="Nenhum formulário"
                  />
                )}

                {open && layer.id === 'screens' && (
                  <NavFlat
                    items={nav.screens}
                    layer="screens"
                    selection={selection}
                    onSelect={select}
                    emptyLabel="Nenhuma tela"
                  />
                )}
              </div>
            )
          })}
        </aside>

        <main className="erp-cat-main">
          <DetailPanel
            layer={selection?.layer}
            item={current}
            erpMap={erpMap}
            erpMapStatus={erpMapStatus}
          />
        </main>
      </div>
    </div>
  )
}
